import * as THREE from 'three';
import type { Vec } from '../destruction/structure';
import type { PhysicsCity } from '../physics/world';
import type { Person, Population, TrafficVehicle } from './population';
import { MILITARY_SPECS, isMilitaryVehicle } from '../render/military-vehicles';
import { BOAT_SPECS } from '../render/boats';
import { MILITARY_BALANCE as BALANCE } from './military-balance';

export type CombatTarget = Person | TrafficVehicle;
export type Weapon = 'rifle' | 'roof-gun' | 'rocket' | 'tank';
interface Projectile {
  power?: number;
  id: number;
  event: number;
  owner: string;
  kind: 'rocket' | 'tank';
  p: THREE.Vector3;
  previous: THREE.Vector3;
  velocity: THREE.Vector3;
  age: number;
  ignore?: TrafficVehicle;
}
interface Trace {
  a: THREE.Vector3;
  b: THREE.Vector3;
  life: number;
  friendly: boolean;
}
/** Bounded game projectiles, exact shared-world occlusion and original actor
 * outcomes. Rendering/cutaways never decide whether a wall blocks a shot. */
export class Combat {
  active = false;
  event = 0;
  shots = 0;
  hits = 0;
  lethalHits = 0;
  projectileImpacts = 0;
  projectiles: Projectile[] = [];
  log: { time: number; owner: string; target?: string; weapon: Weapon; result: string }[] = [];
  private time = 0;
  private serial = 0;
  private budget = 24;
  private epoch = 0;
  private traces: Trace[] = [];
  private line: THREE.LineSegments;
  private positions = new Float32Array(512 * 6);
  private colors = new Float32Array(512 * 6);
  private missile: THREE.InstancedMesh;
  private matrix = new THREE.Matrix4();
  private lookup = new Map<string, CombatTarget>();
  private indexAge = 1;
  constructor(
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage),
    );
    geometry.setAttribute(
      'color',
      new THREE.BufferAttribute(this.colors, 3).setUsage(THREE.DynamicDrawUsage),
    );
    geometry.setDrawRange(0, 0);
    this.line = new THREE.LineSegments(
      geometry,
      new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.8 }),
    );
    this.line.frustumCulled = false;
    physics.scene.add(this.line);
    const shape = new THREE.CylinderGeometry(0.055, 0.09, 0.9, 6);
    shape.rotateX(Math.PI / 2);
    this.missile = new THREE.InstancedMesh(shape, physics.material, 64);
    this.missile.count = 0;
    this.missile.frustumCulled = false;
    physics.scene.add(this.missile);
    this.reset();
  }
  reset(): void {
    this.cancel();
    this.epoch = this.physics.life.epoch;
    this.time = this.serial = this.shots = this.hits = this.lethalHits = this.projectileImpacts = 0;
    this.log = [];
    this.traces = [];
    this.lookup.clear();
    this.indexAge = 1;
  }
  start(): void {
    this.cancel();
    this.active = true;
    this.epoch = this.physics.life.epoch;
  }
  cancel(): void {
    this.active = false;
    this.event++;
    this.projectiles = [];
    this.missile.count = 0;
  }
  private live(target: CombatTarget): boolean {
    return target.category === 'person'
      ? !target.fatal && !['incapacitated', 'falling', 'carried', 'recovering'].includes(target.state)
      : target.state !== 'wreck' && target.health > 0;
  }
  muzzle(person: Person, car?: TrafficVehicle): THREE.Vector3 {
    if (car && isMilitaryVehicle(car.kind) && MILITARY_SPECS[car.kind].gun)
      return new THREE.Vector3(
        0,
        car.kind === 'tank' ? 0.38 : 0.34,
        car.kind === 'tank' ? -4.2 : -1.2,
      ).applyMatrix4(this.population.militaryTurretPose(car, 1));
    const pose = this.population.personPose(person, 'weapon');
    return pose
      ? new THREE.Vector3(0, 0.045, -0.75).applyMatrix4(pose)
      : person.p.clone().add(new THREE.Vector3(0, 1.3, 0));
  }
  targetPoint(target: CombatTarget): THREE.Vector3 {
    return target.p
      .clone()
      .add(new THREE.Vector3(0, target.category === 'person' ? (target.exposedSeat ? 1.55 : 1.2) : 1.1, 0));
  }
  visible(shooter: Person, target: CombatTarget, car?: TrafficVehicle): boolean {
    if (!this.live(target) || target.hidden || shooter === target) return false;
    const from = this.muzzle(shooter, car),
      to = this.targetPoint(target),
      dir = to.clone().sub(from),
      length = dir.length();
    if (length > 230 || length < 0.15) return false;
    dir.divideScalar(length);
    const hit = this.physics.ray(from, dir, length - 0.2, car?.body, {
      physical: true,
      ignoreEntities: true,
    });
    return !hit || hit.entity?.id === target.id;
  }
  private trace(
    from: THREE.Vector3,
    dir: THREE.Vector3,
    length: number,
    owner: string,
    car?: TrafficVehicle,
  ) {
    const geometry = this.physics.ray(from, dir, length, car?.body, { physical: true, ignoreEntities: true }),
      limit = geometry ? from.distanceTo(geometry.point) : length,
      middle = from.clone().addScaledVector(dir, limit / 2),
      candidates = new Set<string>();
    this.population.actorsNear(middle, limit / 2 + 9, (a) => {
      if (a.category !== 'animal' && !a.hidden) candidates.add(a.id);
    });
    // Candidates are spatially bounded. Original articulated bounds handle
    // actual hits and crossfire; no all-pairs soldier visibility loop.
    const entity = this.population.entities?.raycast(
      from,
      dir,
      limit,
      new Set([owner, ...(car ? [car.id] : [])]),
      candidates,
    );
    return entity ?? geometry;
  }
  private record(owner: string, weapon: Weapon, result: string, target?: string): void {
    this.log.push({ time: this.time, owner, weapon, result, target });
    if (this.log.length > 512) this.log.shift();
  }
  private hit(owner: string, weapon: Weapon, id: string, origin: Vec): void {
    const target = this.lookup.get(id);
    if (!target || (target.category === 'person' ? target.fatal : !this.live(target))) return;
    this.hits++;
    if (target.category === 'person') {
      const shooter = this.lookup.get(owner);
      const damage =
        weapon === 'rocket' || weapon === 'tank'
          ? 20
          : shooter?.category === 'person' && shooter.elite
            ? BALANCE.eliteWeaponPower
            : 1;
      target.combatHealth = Math.max(0, (target.combatHealth ?? 1) - damage);
      if (target.combatHealth > 0) {
        this.record(owner, weapon, 'original operator armour absorbed a valid hit', id);
        return;
      }
      target.fatal = true;
      target.hidden = false;
      target.carrier = undefined;
      target.exposedSeat = false;
      target.poseTilt = target.seatHeight = target.seatBlend = undefined;
      this.population.civil?.cancelTask(target, 'Direct projectile casualty');
      this.population.incapacitate(target, origin, 1.6);
      this.population.rail?.cancel(target.id, 'Original passenger struck during combat');
      this.lethalHits++;
      this.record(owner, weapon, 'one valid direct humanoid hit; persistent non-graphic casualty', id);
    } else {
      const armour = isMilitaryVehicle(target.kind)
        ? MILITARY_SPECS[target.kind].armour
        : target.boatFamily
          ? (BOAT_SPECS[target.boatFamily].armour ?? 10)
          : 10;
      const source = this.lookup.get(owner),
        power =
          source?.category === 'person' && source.faction === 'hostile'
            ? BALANCE.hostileOrdnance
            : source?.category === 'person' && source.elite
              ? BALANCE.eliteWeaponPower
              : 1;
      const protection = target.protection ?? 1;
      target.health = Math.max(
        0,
        target.health - (power * (weapon === 'roof-gun' ? 1.4 : 0.6)) / (armour * protection),
      );
      if (target.health <= 0) this.population.disable(target);
      this.record(owner, weapon, 'original vehicle damaged', id);
    }
  }
  fire(shooter: Person, target: CombatTarget, weapon: Weapon = 'rifle', car?: TrafficVehicle): boolean {
    if (!this.active || this.epoch !== this.physics.life.epoch || !this.live(shooter) || this.budget <= 0)
      return false;
    if ((weapon === 'rocket' || weapon === 'tank') && this.projectiles.length >= 64) return false;
    if (!this.visible(shooter, target, car)) return false;
    const from = this.muzzle(shooter, car),
      dir = this.targetPoint(target).sub(from).normalize();
    this.budget--;
    this.shots++;
    shooter.state = car ? 'turret' : 'firing';
    if (weapon === 'rocket' || weapon === 'tank') {
      this.projectiles.push({
        power:
          shooter.faction === 'hostile'
            ? BALANCE.hostileOrdnance
            : shooter.elite
              ? BALANCE.eliteWeaponPower
              : 1,
        id: ++this.serial,
        event: this.event,
        owner: shooter.id,
        kind: weapon,
        p: from,
        previous: from.clone(),
        velocity: dir.multiplyScalar(weapon === 'tank' ? 95 : 52),
        age: 0,
        ignore: car,
      });
      this.record(shooter.id, weapon, 'visible colliding projectile launched');
    } else {
      const hit = this.trace(from, dir, 230, shooter.id, car),
        end = hit?.point ?? from.clone().addScaledVector(dir, 230);
      if (this.traces.length >= 512) this.traces.shift();
      this.traces.push({ a: from, b: end.clone(), life: 0.12, friendly: shooter.faction === 'friendly' });
      if (hit?.entity) this.hit(shooter.id, weapon, hit.entity.id, from);
      else this.record(shooter.id, weapon, hit ? 'stopped by occupied world geometry' : 'miss');
    }
    this.population.audio.play(
      weapon === 'rifle' || weapon === 'roof-gun' ? 'crack' : 'blast',
      weapon === 'rifle' ? 0.18 : 0.28,
      from,
    );
    return true;
  }
  /** Aim a stylised projectile at a visible piece of the shared world. The
   * projectile still collides normally; this is not direct structural damage. */
  fireAtStructure(
    shooter: Person,
    point: Vec,
    weapon: 'rocket' | 'tank',
    car?: TrafficVehicle,
    expectedBuilding?: number,
  ): boolean {
    if (
      !this.active ||
      this.epoch !== this.physics.life.epoch ||
      !this.live(shooter) ||
      this.budget <= 0 ||
      this.projectiles.length >= 64
    )
      return false;
    const from = this.muzzle(shooter, car),
      dir = new THREE.Vector3(point.x, point.y, point.z).sub(from),
      distance = dir.length();
    if (distance < 7 || distance > 230) return false;
    dir.divideScalar(distance);
    const hit = this.physics.ray(from, dir, distance + 1, car?.body, {
      physical: true,
      ignoreEntities: true,
    });
    if (!hit?.cluster && !hit?.dormant) return false;
    if (
      expectedBuilding !== undefined &&
      (hit.cluster?.building.id ?? hit.dormant?.building.id) !== expectedBuilding
    )
      return false;
    if (from.distanceTo(hit.point) < 7) return false;
    this.budget--;
    this.shots++;
    shooter.state = car ? 'turret' : 'firing';
    this.projectiles.push({
      power: shooter.faction === 'hostile' ? 12 : shooter.elite ? 3 : 1,
      id: ++this.serial,
      event: this.event,
      owner: shooter.id,
      kind: weapon,
      p: from,
      previous: from.clone(),
      velocity: dir.multiplyScalar(weapon === 'tank' ? 95 : 52),
      age: 0,
      ignore: car,
    });
    this.population.audio.play('blast', 0.28, from);
    this.record(shooter.id, weapon, 'visible world target; colliding projectile launched');
    return true;
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch) {
      this.cancel();
      return;
    }
    this.time += dt;
    this.budget = 24;
    this.indexAge += dt;
    if (this.indexAge >= 0.25) {
      this.indexAge = 0;
      this.lookup.clear();
      for (const a of [...this.population.people, ...this.population.cars]) this.lookup.set(a.id, a);
    }
    for (let i = this.traces.length - 1; i >= 0; i--) {
      this.traces[i].life -= dt;
      if (this.traces[i].life <= 0) this.traces.splice(i, 1);
    }
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i];
      if (!this.active || p.event !== this.event) {
        this.projectiles.splice(i, 1);
        continue;
      }
      p.previous.copy(p.p);
      p.age += dt;
      const move = p.velocity.clone().multiplyScalar(dt),
        length = move.length(),
        dir = move.divideScalar(length),
        hit = this.trace(p.p, dir, length, p.owner, p.ignore);
      if (hit) {
        if (hit.entity) this.hit(p.owner, p.kind, hit.entity.id, p.p);
        this.physics.blast(
          hit.point,
          (p.kind === 'tank' ? 6.5 : 4.5) * Math.cbrt(p.power ?? 1),
          (p.kind === 'tank' ? 17 : 11) * (p.power ?? 1),
          true,
          'impact',
          0.6,
        );
        this.projectileImpacts++;
        this.record(
          p.owner,
          p.kind,
          'actual collision; shared structural/fire/physical impact',
          hit.entity?.id,
        );
        this.projectiles.splice(i, 1);
      } else {
        p.p.addScaledVector(p.velocity, dt);
        if (p.age > 6) this.projectiles.splice(i, 1);
      }
    }
  }
  render(alpha: number): void {
    let i = 0;
    for (const t of this.traces) {
      this.positions.set([...t.a.toArray(), ...t.b.toArray()], i * 6);
      const c = t.friendly ? [0.72, 0.91, 0.55] : [1, 0.58, 0.32];
      this.colors.set([...c, ...c], i++ * 6);
    }
    this.line.geometry.setDrawRange(0, i * 2);
    this.line.geometry.getAttribute('position').needsUpdate = true;
    this.line.geometry.getAttribute('color').needsUpdate = true;
    this.missile.count = 0;
    for (const p of this.projectiles) {
      const q = new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(0, 0, -1),
        p.velocity.clone().normalize(),
      );
      this.matrix.compose(p.previous.clone().lerp(p.p, alpha), q, new THREE.Vector3(1, 1, 1));
      const n = this.missile.count++;
      this.missile.setMatrixAt(n, this.matrix);
      this.missile.setColorAt(n, new THREE.Color(0xb0b69d));
    }
    if (this.missile.count) {
      this.missile.instanceMatrix.needsUpdate = true;
      this.missile.instanceColor!.needsUpdate = true;
    }
  }
  snapshot(): Record<string, number> {
    return {
      combatShots: this.shots,
      combatHits: this.hits,
      combatLethalHits: this.lethalHits,
      combatProjectiles: this.projectiles.length,
      combatProjectileImpacts: this.projectileImpacts,
    };
  }
  dispose(): void {
    this.cancel();
    this.line.removeFromParent();
    this.line.geometry.dispose();
    (this.line.material as THREE.Material).dispose();
    this.missile.removeFromParent();
    this.missile.geometry.dispose();
  }
}
